import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { LanguageProvider } from './context/LanguageContext';
import { HelmetProvider } from 'react-helmet-async';
import { onCLS, onFCP, onLCP } from 'web-vitals';
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { startAnalytics, track, capturePageview, ANALYTICS_EVENTS } from './lib/analytics';

import Navbar from './components/Navbar';
import SynapsisHero from './components/SynapsisHero';
import SynapsisSolutions from './components/SynapsisSolutions';
import SynapsisPricing from './components/SynapsisPricing';
import SynapsisCTA from './components/SynapsisCTA';
import SynapsisFooter from './components/SynapsisFooter';
import CookieConsent from './components/CookieConsent';
import WorkProcess from './components/WorkProcess';
import TechLogos from './components/TechLogos';
import FAQ from './components/FAQ';
import SEO from './components/SEO';

const PrivacyPolicy = lazy(() => import('./components/PrivacyPolicy'));
const TermsConditions = lazy(() => import('./components/TermsConditions'));
const CookiePolicy = lazy(() => import('./components/CookiePolicy'));

const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="w-8 h-8 border-2 border-accent-500 border-t-transparent rounded-full animate-spin" />
  </div>
);

const SectionDivider = () => (
  <div className="section-divider mx-auto max-w-4xl" aria-hidden="true" />
);

const HomePage = () => {
  return (
    <>
      <SEO />
      <Navbar />
      <main id="main-content">
        <section aria-label="Introduction">
          <SynapsisHero />
        </section>
        <SectionDivider />
        <section id="solutions" aria-label="Solutions">
          <SynapsisSolutions />
        </section>
        <SectionDivider />
        <section id="process" aria-label="Work Process">
          <WorkProcess />
        </section>
        <SectionDivider />
        <TechLogos />
        <SectionDivider />
        <section id="pricing" aria-label="Pricing">
          <SynapsisPricing />
        </section>
        <SectionDivider />
        <section aria-label="FAQ">
          <FAQ />
        </section>
        <SectionDivider />
        <section id="contact" aria-label="Contact us">
          <SynapsisCTA />
        </section>
      </main>
      <SynapsisFooter />
      <CookieConsent />
    </>
  );
};

/**
 * Sends a pageview on every route change.
 *
 * PostHog's automatic capture fires once on load, which in a single-page app
 * means the legal routes would never be counted.
 */
const RouteTracker = () => {
  const location = useLocation();
  useEffect(() => {
    capturePageview();
  }, [location.pathname]);
  return null;
};

const App = () => {
  useEffect(() => {
    document.documentElement.style.scrollBehavior = 'smooth';

    // Starts only if the visitor already accepted on a previous visit; a first
    // visit waits for the banner.
    startAnalytics();

    // These were being measured and thrown away: the old handler posted to
    // window.gtag, which no script on this site has ever defined.
    const reportWebVitals = (metric) => {
      track(ANALYTICS_EVENTS.WEB_VITAL, {
        metric: metric.name,
        value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value),
        rating: metric.rating,
      });
    };

    onCLS(reportWebVitals);
    onFCP(reportWebVitals);
    onLCP(reportWebVitals);
  }, []);

  return (
    <HelmetProvider>
      <LanguageProvider>
        <div className="font-sans bg-bg min-h-screen relative">
          <Router>
            <RouteTracker />
            <Suspense fallback={<LoadingFallback />}>
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/terms-conditions" element={<TermsConditions />} />
                <Route path="/cookie-policy" element={<CookiePolicy />} />
              </Routes>
            </Suspense>
          </Router>
          <Analytics />
          <SpeedInsights />
        </div>
      </LanguageProvider>
    </HelmetProvider>
  );
};

export default App;
